import { useState, useEffect } from "react";
import { API, authFetch } from "@/App";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Switch } from "@/components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/components/ui/tabs";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  Plus,
  Trash2,
  Loader2,
  Tag,
  Hash,
  FileText,
  Search,
  ArrowRight,
  Sparkles,
  MousePointer,
  Check,
  X,
  HelpCircle,
  Regex,
  Locate,
  GripVertical,
} from "lucide-react";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

// Detection type icons and labels
const DETECTION_TYPES = {
  label: { icon: Tag, label: "Label", description: "Champ avec étiquette (ex: SOCIETE: Dupont)" },
  position: { icon: Hash, label: "Position", description: "Position dans l'email (ligne N)" },
  pattern: { icon: FileText, label: "Pattern", description: "Format reconnu (téléphone, email, date...)" },
  context: { icon: Search, label: "Contexte", description: "Mot-clé avant/après la donnée" },
  context_position: { icon: Locate, label: "Position après contexte", description: "Nième élément d'une ligne située après un mot-clé" },
};

// Mapping Rules Table (Mode B)
const MappingRulesTable = ({ rules, columns, patterns, onRulesChange }) => {
  const [editingRule, setEditingRule] = useState(null);

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const addRule = () => {
    const newRule = {
      rule_id: `rule_${Date.now()}`,
      detection_type: "label",
      detection_value: "",
      target_column: columns[0]?.name?.toLowerCase().replace(/\s+/g, '_') || "",
      transformation: "",
      is_regex: false,
      // For context_position type
      line_offset: 1,
      element_position: 1,
      sum_all: false,
    };
    onRulesChange([...rules, newRule]);
    setEditingRule(newRule.rule_id);
  };

  const updateRule = (ruleId, field, value) => {
    onRulesChange(
      rules.map((r) => (r.rule_id === ruleId ? { ...r, [field]: value } : r))
    );
  };

  const deleteRule = (ruleId) => {
    onRulesChange(rules.filter((r) => r.rule_id !== ruleId));
  };

  const getColumnKey = (colName) => {
    return colName.toLowerCase().replace(/\s+/g, '_').replace(/'/g, '').replace(/-/g, '_');
  };

  const handleDragEnd = (event) => {
    const { active, over } = event;
    if (active.id !== over?.id) {
      const oldIndex = rules.findIndex((r) => r.rule_id === active.id);
      const newIndex = rules.findIndex((r) => r.rule_id === over.id);
      onRulesChange(arrayMove(rules, oldIndex, newIndex));
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <p className="text-sm text-zinc-600">
          Définissez comment chaque donnée de l'email correspond à une colonne Excel.
        </p>
        <Button onClick={addRule} size="sm" className="gap-1 bg-orange-500 hover:bg-orange-600">
          <Plus className="w-4 h-4" />
          Nouvelle règle
        </Button>
      </div>

      {rules.length === 0 ? (
        <div className="text-center py-12 border-2 border-dashed border-zinc-200 rounded-lg">
          <FileText className="w-10 h-10 mx-auto mb-3 text-zinc-300" />
          <p className="text-zinc-500">Aucune règle de mapping</p>
          <p className="text-sm text-zinc-400 mt-1">
            Cliquez sur "Nouvelle règle" ou utilisez l'onglet "Apprentissage"
          </p>
        </div>
      ) : (
        <div className="border rounded-lg overflow-hidden">
          <div className="max-h-[400px] overflow-y-auto">
            <DndContext
              sensors={sensors}
              collisionDetection={closestCenter}
              onDragEnd={handleDragEnd}
            >
              <Table>
                <TableHeader>
                  <TableRow className="bg-zinc-50 sticky top-0 z-10">
                    <TableHead className="w-[40px]"></TableHead>
                    <TableHead className="w-[160px]">Détection</TableHead>
                    <TableHead>Configuration</TableHead>
                    <TableHead className="w-[180px]">Colonne cible</TableHead>
                    <TableHead className="w-[150px]">Transformation</TableHead>
                  <TableHead className="w-[50px]"></TableHead>
                </TableRow>
              </TableHeader>
              <SortableContext items={rules.map(r => r.rule_id)} strategy={verticalListSortingStrategy}>
                <TableBody>
                  {rules.map((rule) => (
                    <SortableRuleRow
                      key={rule.rule_id}
                      rule={rule}
                      columns={columns}
                      patterns={patterns}
                      updateRule={updateRule}
                      deleteRule={deleteRule}
                      getColumnKey={getColumnKey}
                      isEditing={editingRule === rule.rule_id}
                    />
                  ))}
                </TableBody>
              </SortableContext>
            </Table>
          </DndContext>
          </div>
        </div>
      )}
    </div>
  );
};

// Sortable Rule Row Component
const SortableRuleRow = ({ rule, columns, patterns, updateRule, deleteRule, getColumnKey, isEditing }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: rule.rule_id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <TableRow 
      ref={setNodeRef} 
      style={style} 
      className={`${isEditing ? "bg-orange-50" : ""} ${isDragging ? "bg-zinc-100" : ""}`}
    >
      <TableCell className="w-[40px]">
        <button
          {...attributes}
          {...listeners}
          className="cursor-grab active:cursor-grabbing p-1 hover:bg-zinc-100 rounded"
        >
          <GripVertical className="w-4 h-4 text-zinc-400" />
        </button>
      </TableCell>
      <TableCell>
        <Select
          value={rule.detection_type}
          onValueChange={(v) => updateRule(rule.rule_id, "detection_type", v)}
        >
          <SelectTrigger className="h-9">
            <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {Object.entries(DETECTION_TYPES).map(([key, { icon: Icon, label, description }]) => (
                            <SelectItem key={key} value={key}>
                              <div className="flex items-center gap-2">
                                <Icon className="w-4 h-4 text-zinc-500" />
                                <span>{label}</span>
                              </div>
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </TableCell>
                    <TableCell>
                      {rule.detection_type === "context_position" ? (
                        <div className="flex items-center gap-2 flex-wrap">
                          <input
                            type="text"
                            value={rule.detection_value || ""}
                            onChange={(e) => updateRule(rule.rule_id, "detection_value", e.target.value)}
                            placeholder="Mot-clé contexte"
                            className="h-9 px-3 py-1 border border-zinc-300 rounded-md text-sm flex-1 min-w-[120px] focus:outline-none focus:ring-2 focus:ring-orange-500"
                          />
                          <span className="text-xs text-zinc-500">+</span>
                          <input
                            type="number"
                            min="1"
                            value={rule.line_offset ?? 1}
                            onChange={(e) => updateRule(rule.rule_id, "line_offset", parseInt(e.target.value) || 1)}
                            className="h-9 w-14 px-2 py-1 border border-zinc-300 rounded-md text-sm text-center focus:outline-none focus:ring-2 focus:ring-orange-500"
                            title="Lignes après"
                          />
                          <span className="text-xs text-zinc-500">ligne(s), élém.</span>
                          <input
                            type="number"
                            min="1"
                            value={rule.element_position ?? 1}
                            onChange={(e) => updateRule(rule.rule_id, "element_position", parseInt(e.target.value) || 1)}
                            className="h-9 w-14 px-2 py-1 border border-zinc-300 rounded-md text-sm text-center focus:outline-none focus:ring-2 focus:ring-orange-500"
                            title="Position élément"
                          />
                          <label className="flex items-center gap-1.5 ml-2 cursor-pointer">
                            <input
                              type="checkbox"
                              checked={rule.sum_all ?? false}
                              onChange={(e) => updateRule(rule.rule_id, "sum_all", e.target.checked)}
                              className="w-4 h-4 rounded border-zinc-300 text-orange-500 focus:ring-orange-500"
                            />
                            <span className="text-xs text-zinc-600 font-medium">Σ Additionner</span>
                          </label>
                        </div>
                      ) : rule.detection_type === "pattern" ? (
                        <div className="flex items-center gap-2">
                          {!rule.is_regex ? (
                            <Select
                              value={rule.detection_value}
                              onValueChange={(v) => updateRule(rule.rule_id, "detection_value", v)}
                            >
                              <SelectTrigger className="h-9 flex-1">
                                <SelectValue placeholder="Choisir..." />
                              </SelectTrigger>
                              <SelectContent>
                                {patterns.map((p) => (
                                  <SelectItem key={p.key} value={p.key}>
                                    {p.name}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          ) : (
                            <Input
                              value={rule.detection_value || ""}
                              onChange={(e) => updateRule(rule.rule_id, "detection_value", e.target.value)}
                              placeholder="Regex personnalisé"
                              className="h-9 flex-1"
                            />
                          )}
                          <TooltipProvider>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <Button
                                  variant={rule.is_regex ? "default" : "outline"}
                                  size="sm"
                                  className={`h-8 w-8 p-0 ${rule.is_regex ? "bg-orange-500" : ""}`}
                                  onClick={() => updateRule(rule.rule_id, "is_regex", !rule.is_regex)}
                                >
                                  <Regex className="w-4 h-4" />
                                </Button>
                              </TooltipTrigger>
                              <TooltipContent>
                                <p>{rule.is_regex ? "Regex personnalisé actif" : "Utiliser regex personnalisé"}</p>
                              </TooltipContent>
                            </Tooltip>
                          </TooltipProvider>
                        </div>
                      ) : rule.detection_type === "position" ? (
                        <Input
                          type="number"
                          min="1"
                          value={rule.detection_value || ""}
                          onChange={(e) => updateRule(rule.rule_id, "detection_value", e.target.value)}
                          placeholder="N° ligne"
                          className="h-9 w-24"
                        />
                      ) : (
                        <Input
                          value={rule.detection_value || ""}
                          onChange={(e) => updateRule(rule.rule_id, "detection_value", e.target.value)}
                          placeholder={rule.detection_type === "label" ? "Nom du label" : "Mot-clé"}
                          className="h-9"
                        />
                      )}
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <ArrowRight className="w-4 h-4 text-zinc-400 flex-shrink-0" />
                        <Select
                          value={rule.target_column}
                          onValueChange={(v) => updateRule(rule.rule_id, "target_column", v)}
                        >
                          <SelectTrigger className="h-9">
                            <SelectValue placeholder="Colonne..." />
                          </SelectTrigger>
                          <SelectContent>
                            {columns.map((col) => (
                              <SelectItem key={col.name} value={getColumnKey(col.name)}>
                                {col.name}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    </TableCell>
                    <TableCell>
                      <Input
                        value={rule.transformation || ""}
                        onChange={(e) => updateRule(rule.rule_id, "transformation", e.target.value)}
                        placeholder="Ex: Diviser par 100"
                        className="h-9 text-sm"
                      />
                    </TableCell>
                    <TableCell>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => deleteRule(rule.rule_id)}
                        className="h-8 w-8 p-0 text-red-500 hover:text-red-700 hover:bg-red-50"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </TableCell>
                  </TableRow>
  );
};

// Email Learning Mode (Mode C)
const EmailLearningMode = ({ exampleEmail, segments, columns, rules, onRulesChange, onEmailChange, onSegment, isSegmenting }) => {
  const [selectedSegment, setSelectedSegment] = useState(null);
  const [targetColumn, setTargetColumn] = useState("");
  const [detectionType, setDetectionType] = useState("label");
  const [transformation, setTransformation] = useState("");

  const getColumnKey = (colName) => {
    return colName.toLowerCase().replace(/\s+/g, '_').replace(/'/g, '').replace(/-/g, '_');
  };

  const handleSegmentClick = (segment) => {
    setSelectedSegment(segment);
    // Pre-select detection type based on segment analysis
    if (segment.has_label) {
      setDetectionType("label");
    } else if (segment.detected_type) {
      setDetectionType("pattern");
    } else {
      setDetectionType("position");
    }
    setTargetColumn("");
    setTransformation("");
  };

  const assignSegmentToColumn = () => {
    if (!selectedSegment || !targetColumn) {
      toast.error("Sélectionnez un segment et une colonne cible");
      return;
    }

    let detectionValue = "";
    let isRegex = false;

    switch (detectionType) {
      case "label":
        detectionValue = selectedSegment.detected_label || selectedSegment.text;
        break;
      case "position":
        detectionValue = String(selectedSegment.line_number);
        break;
      case "pattern":
        detectionValue = selectedSegment.detected_type || "";
        break;
      case "context":
        // Extract context keyword from the line
        detectionValue = selectedSegment.full_line?.split(selectedSegment.text)[0]?.trim() || "";
        break;
    }

    const newRule = {
      rule_id: `rule_${Date.now()}`,
      detection_type: detectionType,
      detection_value: detectionValue,
      target_column: targetColumn,
      transformation: transformation,
      is_regex: isRegex,
    };

    onRulesChange([...rules, newRule]);
    toast.success(`Règle ajoutée: "${selectedSegment.text}" → ${targetColumn}`);
    setSelectedSegment(null);
    setTargetColumn("");
    setTransformation("");
  };

  const getSegmentColor = (segment) => {
    // Check if segment is already mapped
    const isMapped = rules.some(r => {
      if (r.detection_type === "label" && segment.detected_label === r.detection_value) return true;
      if (r.detection_type === "position" && String(segment.line_number) === r.detection_value) return true;
      if (r.detection_type === "pattern" && segment.detected_type === r.detection_value) return true;
      return false;
    });

    if (isMapped) return "bg-emerald-100 border-emerald-300 text-emerald-800";
    if (segment.detected_type) return "bg-blue-50 border-blue-200 text-blue-800";
    if (segment.has_label) return "bg-purple-50 border-purple-200 text-purple-800";
    return "bg-zinc-50 border-zinc-200 text-zinc-700";
  };

  return (
    <div className="space-y-4">
      {/* Email Input */}
      <div className="space-y-2">
        <Label>Email exemple</Label>
        <Textarea
          value={exampleEmail}
          onChange={(e) => onEmailChange(e.target.value)}
          placeholder="Collez un email exemple ici pour créer des règles de mapping par clic..."
          className="min-h-[150px] font-mono text-sm resize-y"
          style={{ maxHeight: '500px' }}
          data-testid="example-email-input"
        />
        <Button
          onClick={onSegment}
          disabled={!exampleEmail.trim() || isSegmenting}
          className="gap-2"
          variant="outline"
        >
          {isSegmenting ? (
            <>
              <Loader2 className="w-4 h-4 animate-spin" />
              Analyse...
            </>
          ) : (
            <>
              <Sparkles className="w-4 h-4" />
              Analyser et segmenter
            </>
          )}
        </Button>
      </div>

      {/* Segmented Email */}
      {segments.length > 0 && (
        <div className="space-y-3">
          <div className="flex items-center gap-2">
            <MousePointer className="w-4 h-4 text-orange-500" />
            <Label>Cliquez sur un segment pour l'associer à une colonne</Label>
          </div>

          <div className="p-4 bg-zinc-50 rounded-lg border border-zinc-200 space-y-2 max-h-[300px] overflow-y-auto">
            {/* Group segments by line */}
            {Object.entries(
              segments.reduce((acc, seg) => {
                if (!acc[seg.line_number]) acc[seg.line_number] = [];
                acc[seg.line_number].push(seg);
                return acc;
              }, {})
            ).map(([lineNum, lineSegments]) => (
              <div key={lineNum} className="flex flex-wrap items-center gap-1">
                <span className="text-xs text-zinc-400 w-8 flex-shrink-0">L{lineNum}</span>
                {lineSegments.map((segment) => (
                  <button
                    key={segment.segment_id}
                    onClick={() => handleSegmentClick(segment)}
                    className={`px-2 py-1 rounded border text-sm font-mono transition-all ${getSegmentColor(segment)} ${
                      selectedSegment?.segment_id === segment.segment_id
                        ? "ring-2 ring-orange-500 ring-offset-1"
                        : "hover:ring-1 hover:ring-orange-300"
                    }`}
                    data-testid={`segment-${segment.segment_id}`}
                  >
                    {segment.has_label && (
                      <span className="text-xs opacity-60 mr-1">{segment.detected_label}:</span>
                    )}
                    {segment.text}
                    {segment.detected_type && (
                      <span className="ml-1 text-xs opacity-60">({segment.detected_type})</span>
                    )}
                  </button>
                ))}
              </div>
            ))}
          </div>

          {/* Legend */}
          <div className="flex flex-wrap gap-4 text-xs text-zinc-500">
            <span className="flex items-center gap-1">
              <span className="w-3 h-3 rounded bg-emerald-100 border border-emerald-300"></span>
              Déjà mappé
            </span>
            <span className="flex items-center gap-1">
              <span className="w-3 h-3 rounded bg-purple-50 border border-purple-200"></span>
              Label détecté
            </span>
            <span className="flex items-center gap-1">
              <span className="w-3 h-3 rounded bg-blue-50 border border-blue-200"></span>
              Pattern reconnu
            </span>
            <span className="flex items-center gap-1">
              <span className="w-3 h-3 rounded bg-zinc-50 border border-zinc-200"></span>
              Non identifié
            </span>
          </div>
        </div>
      )}

      {/* Assignment Panel */}
      {selectedSegment && (
        <div className="p-4 bg-orange-50 rounded-lg border border-orange-200 space-y-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Check className="w-5 h-5 text-orange-600" />
              <span className="font-medium text-orange-900">Assigner ce segment</span>
            </div>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setSelectedSegment(null)}
              className="h-8 w-8 p-0"
            >
              <X className="w-4 h-4" />
            </Button>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label className="text-sm">Segment sélectionné</Label>
              <div className="p-2 bg-white rounded border font-mono text-sm">
                {selectedSegment.has_label && (
                  <span className="text-purple-600">{selectedSegment.detected_label}: </span>
                )}
                <span className="text-zinc-800">{selectedSegment.text}</span>
              </div>
            </div>

            <div className="space-y-2">
              <Label className="text-sm">Type de détection</Label>
              <Select value={detectionType} onValueChange={setDetectionType}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {Object.entries(DETECTION_TYPES).map(([key, { icon: Icon, label }]) => (
                    <SelectItem key={key} value={key}>
                      <div className="flex items-center gap-2">
                        <Icon className="w-4 h-4" />
                        {label}
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label className="text-sm">Colonne cible</Label>
              <Select value={targetColumn} onValueChange={setTargetColumn}>
                <SelectTrigger>
                  <SelectValue placeholder="Choisir une colonne..." />
                </SelectTrigger>
                <SelectContent>
                  {columns.map((col) => (
                    <SelectItem key={col.name} value={getColumnKey(col.name)}>
                      {col.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label className="text-sm">Transformation (optionnel)</Label>
              <Input
                value={transformation}
                onChange={(e) => setTransformation(e.target.value)}
                placeholder="Ex: Formater en majuscules"
              />
            </div>
          </div>

          <Button
            onClick={assignSegmentToColumn}
            disabled={!targetColumn}
            className="w-full gap-2 bg-orange-500 hover:bg-orange-600"
          >
            <Plus className="w-4 h-4" />
            Ajouter cette règle de mapping
          </Button>
        </div>
      )}
    </div>
  );
};

// Main Mapping Editor Component
export default function MappingEditor({ rules, onRulesChange, exampleEmail, onExampleEmailChange }) {
  const [columns, setColumns] = useState([]);
  const [patterns, setPatterns] = useState([]);
  const [segments, setSegments] = useState([]);
  const [isSegmenting, setIsSegmenting] = useState(false);
  const [activeTab, setActiveTab] = useState("table");

  // Fetch columns and patterns
  useEffect(() => {
    const fetchData = async () => {
      try {
        const [columnsRes, patternsRes] = await Promise.all([
          authFetch(`${API}/columns`, {}),
          authFetch(`${API}/admin/patterns`, {}),
        ]);

        if (columnsRes.ok) {
          const data = await columnsRes.json();
          setColumns(data.columns || []);
        }

        if (patternsRes.ok) {
          const data = await patternsRes.json();
          setPatterns(data.patterns || []);
        }
      } catch (error) {
        console.error("Failed to fetch data:", error);
      }
    };

    fetchData();
  }, []);

  // Segment email
  const handleSegmentEmail = async () => {
    if (!exampleEmail.trim()) return;

    setIsSegmenting(true);
    try {
      const response = await authFetch(`${API}/admin/segment-email`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email_content: exampleEmail }),
      });

      if (response.ok) {
        const data = await response.json();
        setSegments(data.segments || []);
        toast.success(`${data.segments?.length || 0} segments détectés`);
      } else {
        toast.error("Erreur lors de l'analyse");
      }
    } catch (error) {
      toast.error("Erreur lors de l'analyse");
    } finally {
      setIsSegmenting(false);
    }
  };

  if (columns.length === 0) {
    return (
      <div className="text-center py-8 text-zinc-500">
        <FileText className="w-10 h-10 mx-auto mb-3 text-zinc-300" />
        <p>Aucun template de colonnes configuré</p>
        <p className="text-sm text-zinc-400 mt-1">
          Importez d'abord un fichier Excel dans la section "Colonnes attendues"
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="mapping-editor">
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid w-full grid-cols-2">
          <TabsTrigger value="table" className="gap-2">
            <FileText className="w-4 h-4" />
            Tableau de mapping
          </TabsTrigger>
          <TabsTrigger value="learn" className="gap-2">
            <MousePointer className="w-4 h-4" />
            Apprentissage
          </TabsTrigger>
        </TabsList>

        <TabsContent value="table" className="mt-4">
          <MappingRulesTable
            rules={rules}
            columns={columns}
            patterns={patterns}
            onRulesChange={onRulesChange}
          />
        </TabsContent>

        <TabsContent value="learn" className="mt-4">
          <EmailLearningMode
            exampleEmail={exampleEmail}
            segments={segments}
            columns={columns}
            rules={rules}
            onRulesChange={onRulesChange}
            onEmailChange={onExampleEmailChange}
            onSegment={handleSegmentEmail}
            isSegmenting={isSegmenting}
          />
        </TabsContent>
      </Tabs>

      {/* Rules Summary */}
      {rules.length > 0 && (
        <div className="p-3 bg-emerald-50 rounded-lg border border-emerald-200">
          <p className="text-sm text-emerald-800">
            <Check className="w-4 h-4 inline mr-1" />
            {rules.length} règle{rules.length > 1 ? "s" : ""} de mapping configurée{rules.length > 1 ? "s" : ""}
          </p>
        </div>
      )}
    </div>
  );
}
